import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleMarkdownShortcut } from '../../utils/textareaMarkdownShortcuts';

const ContactForm = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [formStatus, setFormStatus] = useState(null);
    const { t } = useTranslation();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('sending');

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setFormStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
                setTimeout(() => {
                    onClose();
                    setFormStatus(null);
                }, 2000);
            } else {
                setFormStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setFormStatus('error');
        }
    };

    const handleClose = () => {
        onClose();
        setFormStatus(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold mb-4">{t('help.contact')}</h2>

                {formStatus === 'success' ? (
                    <div className="text-ink-muted mb-4">
                        {t('help.messageSent')}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-ink-muted">
                                    {t('help.nameLabel')}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) =>
                                        handleMarkdownShortcut(e, formData.name, (next) =>
                                            setFormData((prev) => ({ ...prev, name: next }))
                                        )
                                    }
                                    required
                                    className="mt-1 block w-full rounded-md border-edge-strong shadow-sm focus:border-edge focus:ring-ring px-3 py-2"
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-ink-muted">
                                    {t('help.emailLabel')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="mt-1 block w-full rounded-md border-edge-strong shadow-sm focus:border-edge focus:ring-ring px-3 py-2"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-ink-muted">
                                    {t('help.subjectLabel')}
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) =>
                                        handleMarkdownShortcut(e, formData.subject, (next) =>
                                            setFormData((prev) => ({ ...prev, subject: next }))
                                        )
                                    }
                                    required
                                    className="mt-1 block w-full rounded-md border-edge-strong shadow-sm focus:border-edge focus:ring-ring px-3 py-2"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-ink-muted">
                                    {t('help.messageLabel')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) =>
                                        handleMarkdownShortcut(e, formData.message, (next) =>
                                            setFormData((prev) => ({ ...prev, message: next }))
                                        )
                                    }
                                    required
                                    rows="4"
                                    className="mt-1 block w-full rounded-md border-edge-strong shadow-sm focus:border-edge focus:ring-ring px-3 py-2"
                                />
                            </div>
                        </div>

                        {formStatus === 'error' && (
                            <div className="text-red-600 mt-4">
                                {t('help.messageError')}
                            </div>
                        )}

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface border border-edge-strong rounded-full hover:bg-surface-muted"
                            >
                                {t('help.cancelButton')}
                            </button>
                            <button
                                type="submit"
                                disabled={formStatus === 'sending'}
                                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-full hover:bg-primary-hover disabled:opacity-50"
                            >
                                {formStatus === 'sending' ? t('help.sending') : t('help.sendMessage')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ContactForm; 